import { Analytics } from '@cobliteam/analytics';
import { parseVehicleSize, type Vehicle, VehicleSize } from '@cobliteam/common-types';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Input from '@cobliteam/ui/form/Input';
import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

interface SpeedStepBodyProps {
  fleetId?: string;
  /** Called after the limits are applied, to advance to the next step. */
  onCompleted?: () => void;
}

/** Suggested default allowed speed per porte (km/h) — lighter vehicles get a higher default. */
const DEFAULT_KMH: Record<VehicleSize, number> = {
  [VehicleSize.SMALL]: 110,
  [VehicleSize.MEDIUM]: 90,
  [VehicleSize.BIG]: 80,
};
/** Stable porte order shown in the form. */
const PORTE_ORDER: VehicleSize[] = [VehicleSize.SMALL, VehicleSize.MEDIUM, VehicleSize.BIG];
/** Linger after success so the user sees the confirmation before advancing. */
const ADVANCE_DELAY_MS = 900;

/**
 * Inline "set allowed speed by porte" — instead of forcing one speed onto the whole fleet at once, the
 * fleet is grouped by vehicle porte (size: small/medium/big) and each porte gets its own allowed speed,
 * applied only to that porte's vehicles via the `updateVehiclesSpeed` batch mutation. Vehicles without a
 * defined porte are intentionally left out (a disclaimer makes clear the limit hits ALL vehicles of the
 * porte). Per-road (OSM) tuning stays on the dedicated speed page.
 */
const SpeedStepBody = ({ fleetId, onCompleted }: SpeedStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const [done, setDone] = useState(false);
  const [limits, setLimits] = useState<Partial<Record<VehicleSize, string>>>({});
  const { data: vehicles } = useVehicle().getVehicles({});
  const { mutate: updateVehiclesSpeed } = useFuscaVehicle().updateVehiclesSpeed;

  // Group the fleet by porte (size). Vehicles with no/unknown porte are skipped — we only set limits per
  // porte present in the fleet, applying each to every vehicle of that porte.
  const groups = useMemo(() => {
    const bySize = new Map<VehicleSize, Vehicle[]>();
    for (const vehicle of vehicles ?? []) {
      const size = vehicle.size?.toLowerCase() as VehicleSize | undefined;
      if (!(size && PORTE_ORDER.includes(size))) continue;
      const list = bySize.get(size) ?? [];
      list.push(vehicle);
      bySize.set(size, list);
    }
    return PORTE_ORDER.filter((size) => bySize.has(size)).map((size) => ({ size, vehicles: bySize.get(size) ?? [] }));
  }, [vehicles]);

  const limitFor = (size: VehicleSize) => limits[size] ?? String(DEFAULT_KMH[size]);

  const handleApply = () => {
    let applied = false;
    for (const group of groups) {
      const limit = Number(limitFor(group.size));
      if (!Number.isFinite(limit) || limit <= 0 || group.vehicles.length === 0) continue;
      // Fire-and-forget per porte so the user isn't blocked while each batch saves in the background.
      updateVehiclesSpeed({ vehicles: group.vehicles, speedLimitInKmh: limit });
      applied = true;
    }
    if (!applied) return;
    Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'speed' });
    setDone(true);
    window.setTimeout(() => onCompleted?.(), ADVANCE_DELAY_MS);
  };

  if (done) {
    return (
      <Typography variant='text-sm' className='flex items-center gap-1 text-success'>
        <Check size={16} aria-hidden />
        {t('inline.speed_applied')}
      </Typography>
    );
  }

  if (groups.length === 0) {
    return (
      <Typography variant='text-sm' className='text-gray-11'>
        {t('inline.speed_no_porte')}
      </Typography>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      <Typography variant='text-sm' className='text-gray-11'>
        {t('inline.speed_porte_disclaimer')}
      </Typography>
      <div className='flex flex-col gap-2'>
        {groups.map((group) => (
          <div key={group.size} className='flex items-center gap-2'>
            <span className='w-24 font-medium text-gray-12 text-sm'>{parseVehicleSize(group.size)}</span>
            <Input
              className='w-24'
              type='number'
              value={limitFor(group.size)}
              onChange={(event) => setLimits((prev) => ({ ...prev, [group.size]: event.target.value }))}
              placeholder={t('inline.speed_kmh')}
            />
            <span className='text-gray-9 text-xs'>km/h · {t('inline.speed_porte_count', { count: group.vehicles.length })}</span>
          </div>
        ))}
      </div>
      <div>
        <Button onClick={handleApply}>{t('inline.speed_apply')}</Button>
      </div>
    </div>
  );
};

export default SpeedStepBody;
